import { FormEvent, useEffect, useState } from "react";
import { Send } from "lucide-react";
import type { Artwork } from "../types/artwork";
import { EnquiryErrors, validateEnquiry } from "../lib/validation";

interface EnquiryFormProps {
  artwork: Artwork;
}

export function EnquiryForm({ artwork }: EnquiryFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(`I am interested in ${artwork.title}.`);
  const [errors, setErrors] = useState<EnquiryErrors>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "failed">("idle");

  useEffect(() => {
    setMessage(`I am interested in ${artwork.title}.`);
    setStatus("idle");
    setErrors({});
  }, [artwork.id, artwork.title]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validateEnquiry({ artworkId: artwork.id, name, email, message });
    if (!validation.ok) {
      setErrors(validation.errors);
      return;
    }

    setErrors({});
    setStatus("sending");
    try {
      const response = await fetch("/api/enquiries", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(validation.value)
      });
      if (!response.ok) throw new Error(`Enquiry failed with ${response.status}`);
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  }

  return (
    <form className="enquiry-form" onSubmit={handleSubmit}>
      <h3>Enquire about this work</h3>
      <label>
        Name
        <input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" />
        {errors.name && <span className="field-error">{errors.name}</span>}
      </label>
      <label>
        Email
        <input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" />
        {errors.email && <span className="field-error">{errors.email}</span>}
      </label>
      <label>
        Message
        <textarea rows={4} value={message} onChange={(event) => setMessage(event.target.value)} />
        {errors.message && <span className="field-error">{errors.message}</span>}
      </label>
      <button className="primary-button" type="submit" disabled={status === "sending" || status === "sent"}>
        <Send aria-hidden="true" />
        {status === "sending" ? "Sending" : status === "sent" ? "Sent" : "Send enquiry"}
      </button>
      {status === "failed" && <p className="form-note error">The message could not be sent. Please try again.</p>}
      {status === "sent" && <p className="form-note">Thank you. Your enquiry has been recorded.</p>}
    </form>
  );
}
